import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

/**
 * Waiting indicator — three dots pulsing in sequence inside a rounded pill.
 * Drop it wherever a surface waits on something in flight (a first read, a
 * pending reply).
 */
@customElement('schmancy-waiting')
export default class SchmancyWaiting extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				color: inherit;
			}

			:host([color='primary']) {
				color: var(--schmancy-sys-color-primary-default);
			}

			:host([color='on-primary']) {
				color: var(--schmancy-sys-color-primary-on);
			}

			:host([color='secondary']) {
				color: var(--schmancy-sys-color-secondary-default);
			}

			:host([color='on-secondary']) {
				color: var(--schmancy-sys-color-secondary-on);
			}

			:host([color='tertiary']) {
				color: var(--schmancy-sys-color-tertiary-default);
			}

			:host([color='on-tertiary']) {
				color: var(--schmancy-sys-color-tertiary-on);
			}

			:host([color='error']) {
				color: var(--schmancy-sys-color-error-default);
			}

			:host([color='on-error']) {
				color: var(--schmancy-sys-color-error-on);
			}

			:host([color='success']) {
				color: var(--schmancy-sys-color-success-default);
			}

			:host([color='on-success']) {
				color: var(--schmancy-sys-color-success-on);
			}

			:host([color='surface']) {
				color: var(--schmancy-sys-color-surface-default);
			}

			:host([color='on-surface']) {
				color: var(--schmancy-sys-color-surface-on);
			}

			/* Same retarget as spinner.ts: the surface family carries no "variant"
			   ground, and surfaceContainerHighest is the tone M3's retired
			   surfaceVariant mapped to. */
			:host([color='surface-variant']) {
				color: var(--schmancy-sys-color-surface-containerHighest);
			}

			:host([color='on-surface-variant']) {
				color: var(--schmancy-sys-color-surface-onVariant);
			}

			.dot {
				background-color: currentColor;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
				animation: schmancy-waiting-dot-pulse 1.4s ease-in-out infinite;
			}

			.dot:nth-child(1) {
				animation-delay: 0ms;
			}

			.dot:nth-child(2) {
				animation-delay: 200ms;
			}

			.dot:nth-child(3) {
				animation-delay: 400ms;
			}

			@keyframes schmancy-waiting-dot-pulse {
				0%,
				60%,
				100% {
					transform: scale(0.6);
					opacity: 0.4;
				}
				30% {
					transform: scale(1);
					opacity: 1;
				}
			}

			@media (prefers-reduced-motion: reduce) {
				.dot {
					animation: none;
					opacity: 0.8;
				}
			}
		`,
	]

	@property({ type: String, reflect: true })
	color?:
		| 'primary'
		| 'on-primary'
		| 'secondary'
		| 'on-secondary'
		| 'tertiary'
		| 'on-tertiary'
		| 'error'
		| 'on-error'
		| 'success'
		| 'on-success'
		| 'surface'
		| 'on-surface'
		| 'surface-variant'
		| 'on-surface-variant'

	/** Token: 'xxs' 12px, 'xs' 16px, 'sm' 20px, 'md' 24px, 'lg' 32px. Numeric: Tailwind units (each = 4px). */
	@property() size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | number = 'md'

	protected render(): unknown {
		const tokenSizes: Record<string, number> = {
			xxs: 12,
			xs: 16,
			sm: 20,
			md: 24,
			lg: 32,
		}

		let sizeInPx: number
		if (typeof this.size === 'string' && this.size in tokenSizes) {
			sizeInPx = tokenSizes[this.size]
		} else if (typeof this.size === 'number' && !isNaN(this.size)) {
			sizeInPx = this.size * 4
		} else {
			sizeInPx = 24
		}

		const dotSize = Math.max(4, Math.round(sizeInPx / 3))
		const gap = Math.max(2, Math.round(dotSize / 2))
		const paddingBlock = Math.round(dotSize * 0.9)
		const paddingInline = Math.round(dotSize * 1.25)

		const dotStyle = this.styleMap({
			width: `${dotSize}px`,
			height: `${dotSize}px`,
		})

		return html`
			<div
				class="inline-flex items-center rounded-full bg-surface-container"
				style=${this.styleMap({
					gap: `${gap}px`,
					padding: `${paddingBlock}px ${paddingInline}px`,
				})}
			>
				<span class="dot" style=${dotStyle}></span>
				<span class="dot" style=${dotStyle}></span>
				<span class="dot" style=${dotStyle}></span>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-waiting': SchmancyWaiting
	}
}
